import React, { ReactNode, use, useCallback, useEffect, useMemo } from "react";
import { CompositeScreenProps, NavigationProp, useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import { atom, useAtom } from "jotai";
import { newMissionState } from "../atoms/core";
import { useMutation } from "@apollo/client/react";
import { CREATE_MISSION, TERMINATE_MISSION } from "../graphql/core";
import { ClientInfo, ClientProfile, EarningType, Mission, TerminateReason } from "../__generated__/graphql";
import { GetSalaryDetailsQuery } from "../__generated__/operations/graphql";
import {
    createMissionInputFromMission,
    deepRemoveUndefined,
    formatDateForGraphql,
    MAX_HOURS_PER_WEEK,
    MissionWithIjm,
} from "../utils";
import { CreateMissionStackParamList, MissionScreens } from "../navigations/CreateMissionStack";
import { useI18n } from "../hooks/i18n-hooks";
import { AuthManagerContext } from "./AuthManager";
import { FIND_MY_MISSIONS } from "../graphql/home";
import { useTracking } from "../hooks/tracking";
import { Buildable } from "ts-essentials";
import { useCurrentProfile } from "../hooks/use-current-profile";
import { useSetAlert } from "../hooks/use-set-alert";

export enum CreateMissionMode {
    New, // or duplicate
    Update,
}

export const currentClientMissionFeesRate = atom("0");
export const lastSalaryDetailsState = atom<GetSalaryDetailsQuery | null>(null);

type MultiChoiceScreen = {
    screens: MissionScreens[];
    selector: (mission: Mission, profile?: ClientProfile) => MissionScreens | null;
    noHeader?: boolean;
};

type StepInfo = {
    isVisible: boolean;
    noHeader?: boolean;
};

interface CreateMissionScreensContextInterface {
    closeWizard: (force?: boolean) => void;
    validateScreen: (
        screen: MissionScreens,
        innerNavigation?: NavigationProp<CreateMissionStackParamList>,
    ) => Promise<void>;
    totalSteps: number;
    currentStepIndex: (currentStepName: MissionScreens) => number;
    firstStepIndex: number;
    stepsInfo: StepInfo[];
    createMissionMode?: CreateMissionMode;
}

export const CreateMissionScreensContext =
    React.createContext<CreateMissionScreensContextInterface>(
        {} as CreateMissionScreensContextInterface,
    );

type CreateMissionScreensProps = CompositeScreenProps<
    StackScreenProps<RootStackParamList>,
    StackScreenProps<CreateMissionStackParamList>
>;

export type MissionScreensManagerInitialParams = Buildable<Mission>;

type MissionScreensManagerProps = {
    children?: ReactNode | undefined;
    onValidate?: (mission: Buildable<Mission>) => void; // for testing purpose
    initialParams?: MissionScreensManagerInitialParams;
    createMissionMode?: CreateMissionMode;
    testingInitializedData?: Buildable<MissionWithIjm>; // set it to true to skip the initialization of the mission, usefull when jotai state for newMission is already set (storybook , tests )
};

const createMissionCctFeesScreens: MissionScreens[] = ["CreateMissionHotellerieRestaurantFees"];

const cctFeesScreenMapping: Record<string, MissionScreens> = {
    700001: "CreateMissionHotellerieRestaurantFees",
};

/**
 * screenFlow is the definition of the order of the screens of the Create mission wizard.
 * Just put the name of the screen at the wanted index
 * Or if the screen is conditional put an object with with following keys:
 * - screens: an array of screen name that could be at this position.
 * - selector: a function that take a Mission as parameter and return the screen that must be displayed
 * (screen must be one of the screen defined in screens attribute)
 */
export const screensFlow: (MissionScreens | MultiChoiceScreen)[] = [
    "CreateMissionWorker",
    {
        screens: ["CreateMissionTestWorker"],
        selector: (mission): MissionScreens | null => {
            // Vérifier si le travailleur de test a été sélectionné
            if (mission.workerInfo && "isTestWorker" in mission.workerInfo) {
                return "CreateMissionTestWorker";
            }
            return null;
        },
    },
    "CreateMissionWorkingAddress",
    "CreateMissionJobTitle",
    "CreateMissionEarningType",
    {
        screens: ["CreateMissionWorkingTimeMore"],
        selector: (mission): MissionScreens | null => {
            if (mission.currentContract?.earningType !== EarningType.Package) {
                return "CreateMissionWorkingTimeMore";
            }
            return null;
        },
    },
    "CreateMissionDuration",
    "CreateMissionCCT",
    "CreateMissionSalary",
    {
        screens: createMissionCctFeesScreens,
        selector: (mission): MissionScreens | null => {
            if (
                mission.currentContract?.cctContractId &&
                cctFeesScreenMapping[mission.currentContract?.cctContractId]
            ) {
                return cctFeesScreenMapping[mission.currentContract?.cctContractId];
            }
            return null;
        },
    },
    "CreateMissionFees",
    "CreateMissionDeductions",
    "CreateMissionTimeSheet",
    {
        screens: ["CreateMissionSummary", "CreateMissionSimulationSummary"],
        selector: (mission) => {
            if (mission.workerInfo && "isTestWorker" in mission.workerInfo) {
                return "CreateMissionSimulationSummary";
            }
            return "CreateMissionSummary";
        },
    },
    {
        screens: ["CreateContractPreSign"],
        selector: () => {
            return "CreateContractPreSign";
        },
        noHeader: true,
    },
];

export const getInitialMissionScreen = (
    profile: ClientProfile,
): MissionScreens | MultiChoiceScreen => {
    for (const screen of screensFlow) {
        if (typeof screen === "string") {
            return screen;
        } else {
            const nextScreen = screen.selector({} as Mission, profile);
            if (nextScreen) {
                return nextScreen;
            }
        }
    }
    return screensFlow[0];
};

const MissionScreensManager = ({
    children,
    onValidate,
    initialParams = {},
    createMissionMode = CreateMissionMode.New,
    testingInitializedData,
}: MissionScreensManagerProps) => {
    const i18n = useI18n();
    const { setAlert } = useSetAlert();

    const { user, refreshUser } = use(AuthManagerContext);
    const [isMissionInitialized, setIsMissionInitialized] = React.useState(false);
    const navigation = useNavigation<CreateMissionScreensProps["navigation"]>();
    const currentProfile = useCurrentProfile();
    const [mission, setMission] = useAtom(newMissionState);
    const [createMission] = useMutation(CREATE_MISSION, {
        refetchQueries: [FIND_MY_MISSIONS],
    });
    const [terminateMission] = useMutation(TERMINATE_MISSION);
    const { trackEvent } = useTracking();
    const [firstStepIndex, setFirstStepIndex] = React.useState(0);

    const stepsInfo = React.useMemo(() => {
        return screensFlow.map((flowItem) => {
            if (typeof flowItem === "string") {
                return { isVisible: true };
            } else {
                const currentScreen = flowItem.selector(
                    mission as Mission,
                    currentProfile as ClientProfile,
                );
                return { isVisible: !!currentScreen, noHeader: flowItem.noHeader };
            }
        });
    }, [currentProfile, mission]);

    const filteredScreenFlow = useMemo(() => {
        return screensFlow.filter((_, i) => stepsInfo[i].isVisible);
    }, [stepsInfo]);

    const getCurrentStepIndex = useCallback(
        (currentStepName: MissionScreens) => {
            return filteredScreenFlow.findIndex((flowItem) => {
                if (flowItem === currentStepName) {
                    return true;
                } else if (typeof flowItem !== "string") {
                    return flowItem.screens.includes(currentStepName);
                }
                return false;
            });
        },
        [filteredScreenFlow],
    );

    useEffect(() => {
        // effect is rerun in case of hot reload. In this case effect won't be run again
        if (!isMissionInitialized) {
            const clientInfo = user?.clientInfos?.find(
                (clientInfo) => clientInfo.id === currentProfile?.id, // attention ClientInfo id is in fact client profile id.
            ) as ClientInfo;

            if (!clientInfo) {
                return;
            }
            setMission({
                currentContract: {
                    cctDetails: { tempdataCallValues: [] },
                    // by default let it be the max authorized. when choosing the cct, it will be adapted
                    hoursPerWeekFullTime: MAX_HOURS_PER_WEEK,
                    vacationDays: 25,
                    has13thSalary: false,
                    pay13thSalaryWithSalary: true,
                },
                clientInfo: clientInfo,
                legalEmployer: currentProfile?.client?.useLegalEmployer,
                ...deepRemoveUndefined(initialParams),
                ...(testingInitializedData || {}),
            });
            setIsMissionInitialized(true);
            const firstStepName = nextScreenName(-1);
            setFirstStepIndex(getCurrentStepIndex(firstStepName));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeWizard = React.useCallback(
        (force: boolean = false) => {
            if (!mission.workerInfo || force) {
                // in case user did not start editing the mission we do not ask for confirmation
                navigation.popToTop();
            } else {
                let title,
                    description,
                    confirmText,
                    cancelText = i18n.t("createMissionManager.close_alert_cancel");

                if (mission.workerInfo.id === "test-worker-special") {
                    title = i18n.t("createMissionManager.close_alert_title_test_worker");
                    description = i18n.t(
                        "createMissionManager.close_alert_description_test_worker",
                    );
                    confirmText = i18n.t("createMissionManager.close_alert_confirm_test_worker");
                } else if (createMissionMode === CreateMissionMode.Update) {
                    title = i18n.t("createMissionManager.close_alert_title_update");
                    description = i18n.t("createMissionManager.close_alert_description_update");
                    confirmText = i18n.t("createMissionManager.close_alert_confirm_update");
                    cancelText = i18n.t("close");
                } else {
                    title = i18n.t("createMissionManager.close_alert_title");
                    description = i18n.t("createMissionManager.close_alert_description");
                    confirmText = i18n.t("createMissionManager.close_alert_confirm");
                }

                setAlert({
                    title,
                    message: description,
                    buttons: [
                        {
                            text: confirmText,
                            onPress: () => {
                                void trackEvent("click_close_create_mission");
                                navigation.popToTop();
                            },
                            style: "destructive",
                        },
                        {
                            text: cancelText,
                            style: "cancel",
                        },
                    ],
                    options: { cancelable: true },
                });
            }
        },
        [mission.workerInfo, navigation, i18n, createMissionMode, setAlert, trackEvent],
    );

    const nextScreenName = useCallback(
        (currentIndex: number): MissionScreens => {
            const flowItem = filteredScreenFlow[currentIndex + 1];
            if (typeof flowItem === "string") {
                return flowItem;
            } else {
                const nextScreen = flowItem.selector(
                    mission as Mission,
                    currentProfile as ClientProfile,
                );
                if (nextScreen) {
                    return nextScreen;
                }
                return nextScreenName(currentIndex + 1);
            }
        },
        [currentProfile, filteredScreenFlow, mission],
    );

    const validateScreen = React.useCallback(
        async (
            screen: MissionScreens,
            innerNavigation?: NavigationProp<CreateMissionStackParamList>,
        ) => {
            if (onValidate) {
                return onValidate(mission);
            }
            const currentScreenIndex = getCurrentStepIndex(screen);
            let contractId = mission.currentContract?.id;
            // we want to create the mission at the summary step. Onc e mission is created we will
            // then update the mission
            if (screen === "CreateMissionSummary") {
                // we need to create the mission
                const variables = {
                    missionData: createMissionInputFromMission(mission as MissionWithIjm),
                    generateContractAsset: true,
                };
                void trackEvent("click_send_mission");

                if (createMissionMode === CreateMissionMode.Update && mission.id) {
                    const terminateResult = await terminateMission({
                        variables: {
                            missionId: mission.id,
                            data: {
                                reason: TerminateReason.Other,
                                terminationDate: formatDateForGraphql(new Date()),
                            },
                        },
                    });
                    if (terminateResult.error) {
                        throw new Error(i18n.t("createMissionManager.update_mission_failed"));
                    }
                }

                const result = await createMission({
                    variables,
                });
                if (!result.error && result.data?.createMission) {
                    contractId = result.data?.createMission.currentContract?.id;
                    setMission(result.data?.createMission);
                } else {
                    throw new Error(i18n.t("createMissionManager.create_mission_failed"));
                }

                void trackEvent("mission_created");
                await refreshUser?.();
            }

            if (currentScreenIndex < filteredScreenFlow.length) {
                const nextScreen = nextScreenName(currentScreenIndex);

                if (nextScreen === "CreateContractPreSign") {
                    if (contractId) {
                        if (innerNavigation) {
                            innerNavigation.navigate("CreateContractPreSign", { contractId });
                        } else {
                            navigation.navigate("CreateContractPreSign", { contractId });
                        }
                    } else {
                        throw new Error(i18n.t("createMissionManager.create_mission_failed"));
                    }
                } else {
                    navigation.navigate(nextScreen);
                }
            } else {
                // if it is the last step we can display the Done message
                navigation.popToTop();
            }
        },
        [
            onValidate,
            getCurrentStepIndex,
            mission,
            filteredScreenFlow.length,
            trackEvent,
            createMissionMode,
            createMission,
            refreshUser,
            terminateMission,
            i18n,
            setMission,
            nextScreenName,
            navigation,
        ],
    );

    return (
        <CreateMissionScreensContext.Provider
            value={{
                closeWizard,
                validateScreen,
                totalSteps: filteredScreenFlow.length - 1,
                currentStepIndex: getCurrentStepIndex,
                stepsInfo,
                firstStepIndex,
                createMissionMode,
            }}
        >
            {children}
        </CreateMissionScreensContext.Provider>
    );
};

export default MissionScreensManager;
